import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DataRow,
  DataTable,
  useToast,
} from "@breakingthelines/design-system";
import { ArrowLeft } from "@phosphor-icons/react";

import { LoadingSpinner } from "@/components/common/loading-spinner";
import {
  listComplimentaryMemberships,
  listPlatformMembershipTiers,
  revokeMembership,
  setMembershipExpiry,
  type MembershipDTO,
} from "@/lib/admin-api";
import {
  buildTierNameMap,
  daysRemaining,
  groupForMembership,
  membershipSubscriberHandle,
  membershipSubscriberLabel,
  resolveTierName,
} from "@/lib/scholarship";

import { MembershipExpirySheet } from "./membership-expiry-sheet";
import styles from "./scholarships.module.css";

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function expiryLabel(membership: MembershipDTO): string {
  if (!membership.expiresAt) return "Forever";
  const expiresAt = new Date(membership.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return "Forever";
  const remaining = daysRemaining(expiresAt);
  const dateLabel = formatDate(membership.expiresAt);
  if (remaining < 0) return `${dateLabel} · expired`;
  if (remaining === 0) return `${dateLabel} · today`;
  return `${dateLabel} · ${remaining} day${remaining === 1 ? "" : "s"} left`;
}

/**
 * ScholarshipsManageView is the actual manage surface — everything except the
 * standalone page's back link and title. It is rendered live as the
 * "Scholarships" tab on the Users page (see users-list.tsx), which is how
 * users reach it today. ScholarshipsPage below wraps this same view for the
 * redirected /users/scholarships route, Storybook, and this file's own tests.
 */
export function ScholarshipsManageView() {
  const toast = useToast();
  const [memberships, setMemberships] = useState<MembershipDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [revokeTarget, setRevokeTarget] = useState<MembershipDTO | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [expiryTarget, setExpiryTarget] = useState<MembershipDTO | null>(null);
  const [expiryDateInput, setExpiryDateInput] = useState("");
  const [settingExpiry, setSettingExpiry] = useState(false);
  const [tierNameMap, setTierNameMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listComplimentaryMemberships()
      .then((data) => {
        if (cancelled) return;
        setMemberships(data.memberships ?? []);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? "Failed to load scholarships");
        setMemberships([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // Resolves tier_id -> tier name for the Tier column: a comp's MembershipDTO
  // carries a tierId but rarely a populated tierName (see resolveTierName in
  // lib/scholarship.ts), so without this the column would show a raw UUID.
  // A failure here degrades to that UUID rather than blocking the page —
  // tier names are a nice-to-have, not worth surfacing as the page's error.
  useEffect(() => {
    let cancelled = false;
    listPlatformMembershipTiers()
      .then((data) => {
        if (cancelled) return;
        setTierNameMap(buildTierNameMap(data.tiers ?? []));
      })
      .catch(() => {
        // Intentionally silent — see comment above.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleRevoke() {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await revokeMembership(revokeTarget.id);
      toast.success(`${membershipSubscriberLabel(revokeTarget)} no longer has access.`, "Revoked");
      setRevokeTarget(null);
      setReloadKey((v) => v + 1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to revoke", "Revoke failed");
    } finally {
      setRevoking(false);
    }
  }

  function closeExpiryDialog() {
    setExpiryTarget(null);
    setExpiryDateInput("");
  }

  async function handleSetExpiry() {
    if (!expiryTarget || !expiryDateInput) return;
    setSettingExpiry(true);
    try {
      await setMembershipExpiry(expiryTarget.id, `${expiryDateInput}T00:00:00.000Z`);
      toast.success(
        `${membershipSubscriberLabel(expiryTarget)}'s access now ends ${formatDate(expiryDateInput)}.`,
        "End date set",
      );
      closeExpiryDialog();
      setReloadKey((v) => v + 1);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to set an end date",
        "Couldn't set an end date",
      );
    } finally {
      setSettingExpiry(false);
    }
  }

  const expiringSoon = memberships.filter((m) => groupForMembership(m) === "expiring-soon");
  const permanent = memberships.filter((m) => groupForMembership(m) === "permanent");

  return (
    <div className={styles.manageView}>
      <p className={styles.subtitle}>
        Every scholarship membership on the platform, identified by having no Stripe subscription,
        not by a zero amount. Grouped by when access ends.
      </p>

      {loading ? (
        <div className={styles.loadingState}>
          <LoadingSpinner size={20} label="Loading scholarships..." />
        </div>
      ) : null}

      {!loading && error ? (
        <div className={styles.errorState}>
          <p className={styles.errorTitle}>Can&apos;t load scholarships yet</p>
          <p className={styles.errorDetail}>{error}</p>
        </div>
      ) : null}

      {!loading && !error ? (
        <>
          <ScholarshipGroupSection
            title="Expiring soon"
            emptyLabel="No scholarship is time-boxed right now."
            memberships={expiringSoon}
            tierNameMap={tierNameMap}
            onRevoke={setRevokeTarget}
            onSetEndDate={setExpiryTarget}
          />
          <ScholarshipGroupSection
            title="Permanent"
            emptyLabel="No permanent scholarships."
            memberships={permanent}
            tierNameMap={tierNameMap}
            onRevoke={setRevokeTarget}
            onSetEndDate={setExpiryTarget}
          />
        </>
      ) : null}

      {/*
       * Kept as an AlertDialog (rather than a Sheet like the expiry dialog
       * below): it's a pure destructive confirmation, and Base UI's
       * AlertDialogRoot hardcodes `disablePointerDismissal: true` with no
       * prop to override it — an intentional a11y choice for confirmations
       * (accidental outside-click shouldn't revoke someone's access). Escape
       * still closes it.
       */}
      <AlertDialog
        open={revokeTarget != null}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke access</AlertDialogTitle>
            <AlertDialogDescription>
              This ends {revokeTarget ? membershipSubscriberLabel(revokeTarget) : ""}&apos;s access
              immediately. This cannot be undone from here; granting it again is a new grant.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={revoking}
              onClick={() => void handleRevoke()}
            >
              {revoking ? "Revoking…" : "Revoke access"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MembershipExpirySheet
        open={expiryTarget != null}
        onClose={closeExpiryDialog}
        title={expiryTarget?.expiresAt ? "Change end date" : "Set an end date"}
        description={
          expiryTarget
            ? expiryTarget.expiresAt
              ? `Choose a new date for when ${membershipSubscriberLabel(expiryTarget)}'s access ends. It currently ends ${formatDate(expiryTarget.expiresAt)}.`
              : `Choose when ${membershipSubscriberLabel(expiryTarget)}'s access ends. Currently permanent: nothing revokes it until you set a date.`
            : ""
        }
        dateValue={expiryDateInput}
        onDateChange={setExpiryDateInput}
        onSave={() => void handleSetExpiry()}
        saving={settingExpiry}
        saveLabel={expiryTarget?.expiresAt ? "Save new date" : "Set end date"}
      />
    </div>
  );
}

/**
 * Standalone wrapper kept for the redirected /users/scholarships route's
 * history, Storybook, and this file's own tests. The live entry point is the
 * "Scholarships" tab on the Users page — see router.tsx and users-list.tsx.
 */
export function ScholarshipsPage() {
  return (
    <div className={styles.page}>
      <Link to="/users" className={styles.backRow}>
        <ArrowLeft size={14} />
        Back to users
      </Link>

      <h1 className={styles.title}>Scholarships</h1>

      <ScholarshipsManageView />
    </div>
  );
}

function ScholarshipGroupSection({
  title,
  emptyLabel,
  memberships,
  tierNameMap,
  onRevoke,
  onSetEndDate,
}: {
  title: string;
  emptyLabel: string;
  memberships: MembershipDTO[];
  tierNameMap: Map<string, string>;
  onRevoke: (membership: MembershipDTO) => void;
  onSetEndDate: (membership: MembershipDTO) => void;
}) {
  return (
    <section className={styles.groupSection}>
      <h2 className={styles.groupTitle}>{title}</h2>
      <DataTable
        columns="minmax(160px, 1.2fr) minmax(120px, 0.8fr) minmax(180px, 1.2fr) minmax(120px, 0.9fr) minmax(200px, 1.6fr) minmax(90px, 0.6fr) minmax(200px, 1fr)"
        header={["Subscriber", "Tier", "Expiry", "Granted by", "Reason", "Stripe", "Actions"]}
        headerClassName={styles.tableHeader}
      >
        {memberships.map((membership) => {
          const subscriberHandle = membershipSubscriberHandle(membership);
          return (
            <DataRow key={membership.id} className={styles.tableRow}>
              <span className={styles.subscriberCell} data-label="Subscriber">
                <span className={styles.subscriberName}>
                  {membershipSubscriberLabel(membership)}
                </span>
                {subscriberHandle ? (
                  <span className={styles.subscriberHandle}>{subscriberHandle}</span>
                ) : null}
              </span>
              <span className={styles.meta} data-label="Tier">
                {resolveTierName(membership, tierNameMap)}
              </span>
              <span className={styles.meta} data-label="Expiry">
                {expiryLabel(membership)}
              </span>
              <span className={styles.meta} data-label="Granted by">
                {membership.grantedByName || membership.grantedBy || "—"}
              </span>
              <span className={styles.meta} title={membership.reason} data-label="Reason">
                {membership.reason || "—"}
              </span>
              <span
                className={membership.hasStripeSubscription ? styles.stripeYes : styles.stripeNo}
                data-label="Stripe"
                title={
                  membership.hasStripeSubscription
                    ? "Has a Stripe subscription"
                    : "No Stripe subscription: this is what makes it a scholarship"
                }
              >
                {membership.hasStripeSubscription ? "Yes" : "None"}
              </span>
              <div className={styles.actions}>
                <Button variant="outline" size="sm" onClick={() => onSetEndDate(membership)}>
                  {membership.expiresAt ? "Extend" : "Set an end date"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => onRevoke(membership)}>
                  Revoke
                </Button>
              </div>
            </DataRow>
          );
        })}
      </DataTable>
      {memberships.length === 0 ? <div className={styles.emptyState}>{emptyLabel}</div> : null}
    </section>
  );
}
